import { useLocalization } from '@lib/localization';
import { noticeLabelKeys, poiNoticeType } from '@lib/spproject/inspectionStatus';
import type { Inspection } from '@lib/spproject/types';
import {
  Box,
  Chip,
  colors,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { selectCurrentPalette } from '@store/reducers/accessibilitySlice';
import { BarChart3, Calendar, Ruler, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Pie, PieChart, ResponsiveContainer } from 'recharts';
import { StyledDialog, StyledItem } from './Detail.styled';
import OvertimeChart from './OvertimeChart';
import useNoticeColors from '@store/hooks/useNoticeColors';

export type DetailedPOI = {
  info: Inspection.Info
  entry: Inspection.PointOfInterest
};

type PoiDetailModalProps = {
  open: boolean;
  onClose: () => void;

  pois: DetailedPOI[];
  template: Inspection.Info;
  current: Inspection.PointOfInterest;
};

const PoiDetailModal = ({ open, onClose, pois, template, current }: PoiDetailModalProps) => {
  const palette = useSelector(selectCurrentPalette);
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const { t } = useLocalization();
  const noticeColors = useNoticeColors();

  const status = {
    'Error': t(noticeLabelKeys.Error),
    'NotChecked': t(noticeLabelKeys.NotChecked),
    'Warning': t(noticeLabelKeys.Warning),
    'Ok': t(noticeLabelKeys.Ok),
  };

  const { okPOIs, errorPOIs, warningPOIs, notCheckedPOIs, totalPois, successRate } = useMemo(() => {
    const ok: Inspection.PointOfInterest[] = [];
    const error: Inspection.PointOfInterest[] = [];
    const warning: Inspection.PointOfInterest[] = [];
    const notChecked: Inspection.PointOfInterest[] = [];

    pois.forEach(({ entry }) => {
      const s = poiNoticeType(entry);
      // A point with no steps says nothing about its state; it is left out, not counted unchecked.
      if (!s) return;
      if (s === 'Ok') ok.push(entry);
      else if (s === 'Error') error.push(entry);
      else if (s === 'Warning') warning.push(entry);
      else notChecked.push(entry);
    });

    const total = ok.length + error.length + warning.length + notChecked.length;
    const success = total > 0 ? (ok.length / total) * 100 : 0;

    return {
      okPOIs: ok,
      errorPOIs: error,
      warningPOIs: warning,
      notCheckedPOIs: notChecked,
      totalPois: total,
      successRate: success
    };
  }, [pois]);

  const succcesRateChipTextColor = successRate > 75 ? '#fafafa' : successRate >= 50 ? '#000' : '#fafafa';
  const succcesRateChipBackgroundColor = successRate > 75 ? colors.green[500] : successRate >= 50 ? colors.yellow[500] : colors.red[500];

  const sortedPois = useMemo(() => {
    const time = (date: Date) => (date instanceof Date ? date : new Date(date)).getTime();
    return [...pois].sort((a, b) => time(b.info.dateModified) - time(a.info.dateModified));
  }, [pois]);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {template?.name ?? '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {template?.id ?? '-'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ mb: 2, mt: 2, p: 2, borderRadius: 2 }}>
          <Stack direction="row" spacing={3} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              #{current.order}
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <Box sx={{ width: 60, height: 60 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Ok', value: okPOIs.length, fill: palette.success },
                        { name: 'Warning', value: warningPOIs.length, fill: palette.warning },
                        { name: 'Error', value: errorPOIs.length, fill: palette.error },
                        { name: 'NotChecked', value: notCheckedPOIs.length, fill: palette.secondary }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={12}
                      outerRadius={22}
                      paddingAngle={2}
                      dataKey="value"
                      stroke={theme.palette.divider}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Chip
                label={`${Math.round(successRate)}%`}
                size="small"
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  fontSize: '12px',
                  height: 20,
                  backgroundColor: succcesRateChipBackgroundColor,
                  '& .MuiChip-label': {
                    px: 0.5,
                    color: succcesRateChipTextColor,
                  },
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={500} mb={0.5}>
                {current?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {current?.id}
              </Typography>
            </Box>

            <Tooltip title={t('Project_TotalInspections')}>
              <Box textAlign="center">
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                  <Ruler size={16} color="#2196f3" />
                  <Typography variant="h6" fontWeight={600}>
                    {totalPois}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t('Project_TotalInspections')}
                </Typography>
              </Box>
            </Tooltip>
          </Stack>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={(_, val) => setTabValue(val)}>
            <Tab
              icon={<Calendar size={16} />}
              label={t('Project_InspectionHistory')}
              iconPosition="start"
              sx={{ textTransform: 'none' }} />
            <Tab
              icon={<BarChart3 size={16} />}
              label={t('Inspections_Performance')}
              iconPosition="start"
              sx={{ textTransform: 'none' }} />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box>
            {sortedPois.map((poi) => {
              const noticeType = poiNoticeType(poi.entry) ?? 'NotChecked';

              return (
                <StyledItem key={`${poi.info.id}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="caption" color="text.primary">
                        {poi.info.name}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {poi.info.id}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {poi.info.dateModified instanceof Date
                          ? poi.info.dateModified.toAgoString()
                          : new Date(poi.info.dateModified).toAgoString()}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={status[noticeType]}
                    size="small"
                    sx={{
                      backgroundColor: noticeColors[noticeType],
                      color: '#fafafa',
                      fontSize: '11px',
                    }}
                  />
                </StyledItem>
              );
            })}
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ height: 400, width: '100%' }}>
            <OvertimeChart
              pois={pois}
            />
          </Box>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default PoiDetailModal;
